import { formatRelative } from 'date-fns';
import React, { Fragment, useState } from 'react';

import { FromStore } from '@cartwhl/common/types';
import Text from '@cartwhl/ui/base/Text';
import Button from '@cartwhl/ui/input/Button';

export default function UploadError({
  createdAt,
  footer,
  isLatestError,
  errorReasons,
  primaryIcon,
  onTryAgainClick,
  title = 'File failed to process',
}: {
  createdAt?: string;
  footer?: React.ReactNode;
  isLatestError?: boolean;
  errorReasons?: FromStore<string[]>;
  primaryIcon: React.ReactNode;
  onTryAgainClick?: () => void;
  title?: string;
}) {
  const [showErrorDetails, setShowErrorDetails] = useState(isLatestError);

  const dateParts = createdAt
    ? formatRelative(createdAt, new Date()).split(' at ')
    : undefined;

  return (
    <>
      <div
        className={`rounded-2xl p-4 ${
          isLatestError ? 'bg-red/20' : 'bg-gray-15 dark:bg-gray-75'
        }`}
      >
        <div
          className="flex justify-between"
          onClick={() => setShowErrorDetails((v) => !v)}
        >
          <div className="flex items-center gap-3 text-left">
            {primaryIcon}
            <div className="flex flex-col items-start justify-center">
              <Text as="p" variant="studio/body/medium/sm">
                {title}
              </Text>
              {errorReasons?.length && (
                <button>
                  <Text
                    variant="studio/body/medium/sm"
                    className={`${
                      isLatestError
                        ? 'text-red'
                        : 'text-gray-60 dark:text-gray-45 dark:hover:text-gray-15'
                    }`}
                  >
                    {showErrorDetails ? 'Hide' : 'Show'} details
                  </Text>
                </button>
              )}
            </div>
          </div>
          {isLatestError && onTryAgainClick ? (
            <Button
              variant="primary"
              size="sm"
              className="bg-red hover:bg-red/80 dark:hover:bg-red/80 text-white"
              onClick={onTryAgainClick}
            >
              Try again
            </Button>
          ) : dateParts ? (
            <div>
              <Text
                variant="studio/body/medium/sm"
                className="text-gray-60 dark:text-gray-45 text-right"
                as="div"
              >
                {dateParts[0]}
              </Text>
              {dateParts[1] && (
                <Text
                  variant="studio/body/medium/sm"
                  className="text-gray-60 dark:text-gray-45 text-right"
                  as="div"
                >
                  {dateParts[1].replace(' ', '').toLowerCase()}
                </Text>
              )}
            </div>
          ) : (
            <></>
          )}
        </div>
        {showErrorDetails && errorReasons?.length && (
          <div
            className={`mt-4 rounded-lg ${
              isLatestError ? 'bg-red/10' : 'bg-gray-90/5 dark:bg-gray-15/10'
            }`}
          >
            {errorReasons?.map((r, i) => (
              <Fragment key={r}>
                <div className="flex gap-3 p-4 text-sm text-red-500">
                  <svg
                    className="flex-none"
                    width="22"
                    height="21"
                    viewBox="0 0 22 21"
                    fill="#FF3F56"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path d="M9.9917 13.1982C9.57666 13.1982 9.22803 12.8496 9.22803 12.4429V4.08398L9.29443 2.84717L8.87939 3.34521L7.60938 4.69824C7.47656 4.84766 7.27734 4.92236 7.08643 4.92236C6.71289 4.92236 6.40576 4.64844 6.40576 4.2583C6.40576 4.05908 6.48877 3.90967 6.62988 3.76855L9.41895 1.0874C9.60986 0.888184 9.79248 0.821777 9.9917 0.821777C10.1992 0.821777 10.3818 0.888184 10.5728 1.0874L13.3618 3.76855C13.5029 3.90967 13.5776 4.05908 13.5776 4.2583C13.5776 4.64844 13.2705 4.92236 12.897 4.92236C12.7061 4.92236 12.5151 4.84766 12.3823 4.69824L11.104 3.33691L10.6973 2.84717L10.7637 4.08398V12.4429C10.7637 12.8496 10.415 13.1982 9.9917 13.1982ZM5.21875 19.814C3.42578 19.814 2.49609 18.8926 2.49609 17.1245V8.94824C2.49609 7.18018 3.42578 6.25879 5.21875 6.25879H7.48486V7.88574H5.31836C4.54639 7.88574 4.12305 8.28418 4.12305 9.08936V16.9834C4.12305 17.7886 4.54639 18.187 5.31836 18.187H10.8882C10.7139 18.7681 10.7803 19.3408 11.0542 19.814H5.21875ZM17.4956 10.0522C17.2466 9.94434 16.9644 9.87793 16.6821 9.87793C16.3999 9.87793 16.1177 9.94434 15.8687 10.0522V9.08936C15.8687 8.28418 15.437 7.88574 14.6733 7.88574H12.5234V6.25879H14.7729C16.5659 6.25879 17.4956 7.18018 17.4956 8.94824V10.0522ZM13.0879 19.814C12.3823 19.814 11.9258 19.2993 11.9258 18.6519C11.9258 18.4609 11.9756 18.2617 12.0835 18.0791L15.6777 11.646C15.8936 11.2559 16.292 11.0566 16.6904 11.0566C17.0806 11.0566 17.4624 11.2476 17.6865 11.646L21.2891 18.0791C21.397 18.2617 21.4468 18.4609 21.4468 18.6519C21.4468 19.2993 20.9819 19.814 20.2764 19.814H13.0879ZM16.6821 16.5269C17.0059 16.5269 17.23 16.311 17.2466 16.0039L17.313 13.688C17.3213 13.3228 17.064 13.0654 16.6821 13.0654C16.3086 13.0654 16.0513 13.3228 16.0596 13.688L16.126 16.0039C16.1426 16.311 16.3667 16.5269 16.6821 16.5269ZM16.6821 18.5605C17.1055 18.5605 17.4292 18.2368 17.4375 17.8218C17.4375 17.415 17.1055 17.083 16.6821 17.083C16.2754 17.083 15.9351 17.415 15.9351 17.8218C15.9351 18.2368 16.2671 18.5605 16.6821 18.5605Z" />
                  </svg>
                  <Text
                    variant="studio/body/medium/sm"
                    as="div"
                    className="text-gray-75 dark:text-gray-30 overflow-hidden break-words"
                  >
                    {r}
                  </Text>
                </div>
                {i !== errorReasons.length - 1 && (
                  <div className="bg-gray-90/10 mx-4 h-px" />
                )}
              </Fragment>
            ))}
          </div>
        )}
        {showErrorDetails && footer}
      </div>
    </>
  );
}
